import { Grid, InputAdornment, MenuItem } from '@mui/material'
import React, { useMemo } from 'react'
import TransformDisplayToEdition from '../../../../components/form/TransformDisplayToEdition'
import HasPermission from '../../../../components/permissions/HasPermission'
import mapEnumForSelect from '../../../../utils/mapEnumForSelect'
import {
  AdGroupCreateInput,
  AdFormat,
  AdGroupInput,
  ChannelType,
  PriorityListQuery,
  Network,
} from '../../../../__generated__/graphql'
import AsyncAutoCompleteCampaignField from '../../create/AsyncAutoCompleteCampaignField'
import { useUserPermissions } from '../../../company/userPermissions'
import ButtonsField from '../../../../components/form/ButtonsField'
import { useFormikContext } from 'formik'
import { isNil } from 'lodash-es'
import { useAppTranslations } from '../../../../utils/hooks/useAppTranslations'
import RedirectUrlUtmGenerator from './RedirectUrlUtmGenerator'
import UrlRedirectCheck from '../../../../components/form/UrlRedirectCheck'

export interface InfosFieldsProps {
  adgroup: AdGroupInput | AdGroupCreateInput
  creatingAdgroup?: boolean
  canEdit?: boolean
  priorityList: PriorityListQuery['campaign']['advertiser']['reseller']['priorities']
  hideCampaignSelector?: boolean
  showCommission?: boolean
}

const InfosFields: React.FC<InfosFieldsProps> = ({
  adgroup,
  creatingAdgroup = false,
  canEdit = true,
  priorityList,
  hideCampaignSelector = false,
  showCommission = false,
}): JSX.Element => {
  const t = useAppTranslations()
  const { banner, web } = useUserPermissions()
  const formik = useFormikContext<AdGroupCreateInput & { commission?: number | null }>()
  const initialCampaignId = formik.initialValues.campaignId

  const adGroupPriorityLabel = useMemo(() => {
    if (formik.values.format === AdFormat.Banner) return t('adgroup.alwaysFirst')
    if (!isNil(formik.values.priorityId)) return priorityList.find((p) => p.id === formik.values.priorityId)?.name
  }, [formik.values.format, formik.values.priorityId, priorityList])

  const adgroupFormats = mapEnumForSelect(AdFormat).filter((format) => {
    switch (format.label) {
      case AdFormat.Banner:
        return banner
      case AdFormat.Web:
        return web
      default:
        return true
    }
  })

  const networkOptions = useMemo(
    () =>
      Object.values(Network).map((value) => ({
        value,
        label: t(`enum.Network.${value}`),
      })),
    [t]
  )

  const channelOptions = useMemo(
    () =>
      Object.values(ChannelType).map((value) => ({
        value,
        label: t(`enum.ChannelType.${value}`),
      })),
    [t]
  )

  const adgroupFormatsOptions = adgroupFormats.map((format, i) => (
    <MenuItem value={format.value} key={i}>
      {format.label}
    </MenuItem>
  ))

  const currentChannels = useMemo((): string[] => {
    const channels = []
    if (adgroup?.channelEmail) channels.push(ChannelType.Email)
    if (adgroup?.channelWeb) channels.push(ChannelType.Web)
    return channels
  }, [adgroup, ChannelType])

  return (
    <Grid container spacing={1}>
      {creatingAdgroup && (
        <Grid size={{ md: 3, xs: 12 }}>
          <TransformDisplayToEdition
            inputType="input"
            type={'text'}
            name={'name'}
            required={true}
            label={'commons.name'}
            text={adgroup.name}
            disableHoverEdition={creatingAdgroup}
            editable={canEdit}
          />
        </Grid>
      )}
      {creatingAdgroup && (
        <HasPermission permissionRequired="banner" roleRequired={'edit'}>
          <Grid size={3}>
            <TransformDisplayToEdition
              inputType={'select'}
              withNone={false}
              name={'format'}
              label={'adgroup.format'}
              text={adgroup.format}
              disableHoverEdition={creatingAdgroup}
              required
              editable={canEdit}
            >
              {adgroupFormatsOptions}
            </TransformDisplayToEdition>
          </Grid>
        </HasPermission>
      )}
      <HasPermission permissionRequired="superAdmin">
        <Grid size={3}>
          <TransformDisplayToEdition
            inputType="select"
            name="network"
            required={true}
            label="adgroup.network"
            text={adgroup.network?.toLowerCase() || ''}
            withNone={false}
            disableHoverEdition={creatingAdgroup}
            options={networkOptions}
          />
        </Grid>
      </HasPermission>
      {adgroup.network === Network.Ecommerce && (
        <HasPermission permissionRequired="web" roleRequired="edit">
          <Grid size={3}>
            <ButtonsField
              name={'channels'}
              label={'adgroup.channels'}
              options={channelOptions}
              defaultValues={currentChannels}
              editable={canEdit}
              tooltipText="adgroup.channelsHelper"
            />
          </Grid>
        </HasPermission>
      )}
      <HasPermission permissionRequired="adgroupPriority" roleRequired="edit">
        {priorityList.length > 0 && (
          <Grid size={3}>
            <TransformDisplayToEdition
              inputType="select"
              name="priorityId"
              label="commons.priority"
              text={adGroupPriorityLabel}
              editable={canEdit && formik.values.format !== AdFormat.Banner}
              disableHoverEdition={creatingAdgroup}
              withNone={false}
              options={priorityList.map((priority) => ({ value: priority.id, label: priority.name }))}
            />
          </Grid>
        )}
      </HasPermission>
      {!creatingAdgroup && (
        <Grid size={{ md: 3, xs: 12 }}>
          <TransformDisplayToEdition
            inputType={'inputFieldArray'}
            type={'text'}
            name={'impressionPixels'}
            label={'adgroup.details.impressionPixels'}
            text={(adgroup as AdGroupInput).impressionPixels.join('\n')}
            disableHoverEdition
            editable={canEdit}
          />
        </Grid>
      )}
      {!creatingAdgroup && (
        <HasPermission permissionRequired="superAdmin">
          <Grid size={3}>
            <TransformDisplayToEdition
              inputType={'inputFieldArray'}
              type={'text'}
              name={'clickPixels'}
              label={'adgroup.details.clickPixels'}
              text={(adgroup as AdGroupInput).clickPixels.join('\n')}
              disableHoverEdition
              editable={canEdit}
            />
          </Grid>
        </HasPermission>
      )}
      {!creatingAdgroup && showCommission && (
        <HasPermission permissionRequired="superAdmin">
          <Grid size={1}>
            <TransformDisplayToEdition
              inputType="input"
              type="number"
              name="commission"
              label="adgroup.details.commission"
              text={formik.values.commission == null ? null : `${formik.values.commission}%`}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              variant={creatingAdgroup ? 'outlined' : 'standard'}
              disableHoverEdition={creatingAdgroup}
              editable={canEdit}
            />
          </Grid>
        </HasPermission>
      )}
      <Grid size={12}>
        <Grid container direction={{ md: 'row', xs: 'column' }} spacing={1} wrap="wrap">
          <Grid size={{ md: 'auto', xs: 12 }} sx={{ mt: { md: 0.75, xs: 0 } }}>
            <RedirectUrlUtmGenerator
              redirectUrl={formik.values.redirectUrl ?? ''}
              disabled={!canEdit}
              onApply={(generatedUrl) => formik.setFieldValue('redirectUrl', generatedUrl)}
            />
          </Grid>
          <Grid size={{ md: 'grow', xs: 12 }}>
            <TransformDisplayToEdition
              inputType="input"
              type={'text'}
              name={'redirectUrl'}
              required={true}
              label={'adgroup.redirectUrl'}
              text={adgroup.redirectUrl}
              multiline={true}
              tooltipText={'adgroup.redirectUrlHelper'}
              disableHoverEdition={creatingAdgroup}
              variant={creatingAdgroup ? 'outlined' : 'standard'}
              editable={canEdit}
            />
            <UrlRedirectCheck name="redirectUrl" />
          </Grid>
        </Grid>
      </Grid>
      {creatingAdgroup && !initialCampaignId && !hideCampaignSelector && (
        <Grid size={{ md: 4, xs: 12 }}>
          <AsyncAutoCompleteCampaignField />
        </Grid>
      )}
    </Grid>
  )
}
export default InfosFields
